import { memo, useRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { AuctionCategory } from "@/types/auction";

interface CategoryTabsProps {
  categories: AuctionCategory[];
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
}

const CategoryTabs = memo(({ categories, selectedCategory, onSelectCategory }: CategoryTabsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  // Update indicator position when selection changes
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Defer layout reads to avoid forced synchronous reflow
    const rafId = requestAnimationFrame(() => {
      if (!containerRef.current) return;
      const activeButton = containerRef.current.querySelector('[data-active="true"]') as HTMLElement;
      if (activeButton) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        setIndicatorStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [selectedCategory, categories]);

  return (
    <div className="overflow-x-auto scrollbar-hide">
      <div 
        ref={containerRef}
        className="relative flex gap-1 p-1 bg-secondary/30 rounded-lg w-max"
      >
        {/* Sliding Indicator */}
        <div
          className="absolute top-1 bottom-1 bg-primary rounded-md transition-all duration-300 ease-out"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
        
        {/* All Button */}
        <button
          onClick={() => onSelectCategory(null)}
          data-active={selectedCategory === null}
          className={cn(
            "relative z-10 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap",
            selectedCategory === null
              ? "text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Alle
        </button>
        
        {/* Category Buttons */}
        {categories.map((category) => (
          <button
            key={category.name}
            onClick={() => onSelectCategory(category.name)}
            data-active={selectedCategory === category.name}
            className={cn(
              "relative z-10 flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap",
              selectedCategory === category.name
                ? "text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <img 
              src={category.icon} 
              alt="" 
              className="w-4 h-4"
              style={{ imageRendering: 'pixelated' }}
              loading="lazy"
            />
            <span className="hidden sm:inline">{category.displayName}</span>
          </button>
        ))}
      </div>
    </div>
  );
});

CategoryTabs.displayName = 'CategoryTabs';

export default CategoryTabs;
